
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const RECAPTCHA_SCRIPT_ID = 'scrambl-recaptcha-script';
let recaptchaScriptPromise: Promise<void> | null = null;

function loadReCaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA is not available'));
  }

  // Check if grecaptcha enterprise is already fully loaded and ready
  if (window.grecaptcha?.enterprise?.execute) {
    return Promise.resolve();
  }

  if (recaptchaScriptPromise) {
    return recaptchaScriptPromise;
  }

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      // Script exists - wait for grecaptcha to be ready
      const checkReady = () => {
        if (window.grecaptcha?.enterprise?.execute) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_SCRIPT_ID;
    // Load with render=siteKey for automatic badge and script initialization
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Wait for grecaptcha.enterprise to be available
      const checkReady = () => {
        if (window.grecaptcha?.enterprise?.execute) {
          resolve();
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

/**
 * Mint a reCAPTCHA token. Tokens are single-use and expire after ~2 minutes,
 * so callers should execute again immediately before each submit rather than
 * reusing the token minted on mount.
 */
function executeReCaptcha(siteKey: string, action: string): Promise<string> {
  return loadReCaptchaScript(siteKey).then(
    () =>
      new Promise<string>((resolve, reject) => {
        if (!window.grecaptcha?.enterprise) {
          reject(new Error('reCAPTCHA is not available'));
          return;
        }
        window.grecaptcha.enterprise.ready(() => {
          if (!window.grecaptcha?.enterprise) {
            reject(new Error('reCAPTCHA is not available'));
            return;
          }
          window.grecaptcha.enterprise.execute(siteKey, { action }).then(resolve, reject);
        });
      }),
  );
}

export interface ReCaptchaHandle {
  /** Mint a fresh, unused token. Returns null if reCAPTCHA is unavailable. */
  execute: () => Promise<string | null>;
}

export interface ReCaptchaProps {
  siteKey: string;
  /** Receives the token minted on mount. Omit when the parent mints via ref instead. */
  onChange?: (token: string | null) => void;
  action?: string;
  className?: string;
  /**
   * Mint a token on mount. Callers that mint at submit time via the ref should
   * pass false, otherwise every page view burns an unused reCAPTCHA assessment.
   */
  executeOnMount?: boolean;
  ref?: Ref<ReCaptchaHandle>;
}

export function ReCaptcha({
  siteKey,
  onChange,
  action = 'submit',
  className,
  executeOnMount = true,
  ref,
}: ReCaptchaProps) {
  const onChangeRef = useRef(onChange);
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const executedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const refresh = async (): Promise<string | null> => {
    try {
      const token = await executeReCaptcha(siteKey, action);
      setIsReady(true);
      setLoadError(null);
      onChangeRef.current?.(token);
      return token;
    } catch {
      setLoadError('Unable to load CAPTCHA. Please refresh.');
      onChangeRef.current?.(null);
      return null;
    }
  };

  // Latest-ref so the imperative handle stays stable across renders.
  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  });

  useImperativeHandle(ref, () => ({ execute: () => refreshRef.current() }), []);

  // Load the script so the indicator resolves, and optionally mint a warm-up
  // token for parents that gate their submit button on having one.
  useEffect(() => {
    const executionKey = `${siteKey}|${action}`;
    if (executedKeyRef.current === executionKey) return;
    executedKeyRef.current = executionKey;

    if (executeOnMount) {
      void refreshRef.current();
      return;
    }

    void loadReCaptchaScript(siteKey).then(
      () => setIsReady(true),
      () => setLoadError('Unable to load CAPTCHA. Please refresh.'),
    );
  }, [siteKey, action, executeOnMount]);

  if (loadError) {
    return <p className="text-xs text-destructive">{loadError}</p>;
  }

  // Score-based reCAPTCHA is invisible - show a subtle indicator.
  // The Google ToS / Privacy Policy links are required when the floating
  // badge is hidden via CSS (see .grecaptcha-badge rule in index.css).
  return (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground', className)}>
      <ShieldCheckIcon className={cn('w-4 h-4 shrink-0', isReady ? 'text-green-600' : 'text-muted-foreground')} />
      <span>
        {isReady ? 'Protected by reCAPTCHA' : 'Loading verification...'}
        {isReady && (
          <>
            {' — '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Privacy
            </a>
            {' & '}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              Terms
            </a>
          </>
        )}
      </span>
    </div>
  );
}
